import { addFlux, AddFluxHOCProps } from 'components/wrappers/WithFlux';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import AgendaPopover from 'modules/common/Navbar/popovers/AgendaPopover/AgendaPopover';
import formatDate from 'utils/formatDate';
import React, { Component } from 'react';
import Analytics from '@withlocals/withlocals-analytics';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import fluxxorConnect from '../../../../../hoc/fluxxorConnect';
import { showAvailabilityFilter } from 'utils/availabilityFilterHelper';
import { compose } from 'recompose';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Filters } from 'model/Filters';
import { analyticsTrack } from 'utils/analytics';
import { AVAILABILITY_FILTER_OPTIONS } from 'data/subOptions/availabilitySubOptions';
import { getFilter } from 'data/filters';

type AgendaPopoverInnerProps = AddFluxHOCProps & { showAvailabilityOptions: { show: boolean; showNow: boolean }; filters: Filters; nowFilterEnabled: boolean } & RouteComponentProps;
type AgendaPopoverOuterProps = {
  initialDateFrom?: Date;
  initialDateTo?: Date;
  onClose: (callback: () => void, source: string) => void;
  noBackdrop?: boolean;
};

type AgendaPopoverProps = AgendaPopoverInnerProps & AgendaPopoverOuterProps;

interface AgendaPopoverState {
  startDate: Date;
  endDate: Date;
}

function trackAvailabilityButton(value) {
  Analytics.track('Search page', 'Apply', value);
}

class AgendaPopoverContainer extends Component<AgendaPopoverProps, AgendaPopoverState> {

  state = {
    startDate: this.props.initialDateFrom,
    endDate: this.props.initialDateTo,
  };

  isPristine() {
    const { startDate, endDate } = this.state;
    const { initialDateFrom, initialDateTo } = this.props;
    return startDate === initialDateFrom && endDate === initialDateTo;
  }

  onApply = (source: string) => (e?: any) => {
    e?.stopPropagation();

    if (this.isPristine()) {
      this.props.onClose(() => {
      }, source);
      return;
    }

    const dates = {
      selectedFrom: !!this.state.startDate ? formatDate(this.state.startDate, 'yyyy-MM-dd') : null,
      selectedTo: !!this.state.endDate ? formatDate(this.state.endDate, 'yyyy-MM-dd') : null,
    };

    const filtersToAdd = [];
    const dateFromFilter = getFilter('date.from');
    dateFromFilter.value = dates.selectedFrom || null;
    filtersToAdd.push(dateFromFilter);

    const dateToFilter = getFilter('date.to');
    dateToFilter.value = dates.selectedTo || null;
    filtersToAdd.push(dateToFilter);

    this.props.flux.actions.filters.setDates(dates);
    this.props.flux.actions.experiencefilter.setDate({ date: dates.selectedFrom });
    
    Analytics.track('Search page', 'Filter date', { dates, now: false });

    this.props.onClose(() => routeToSearchURL({ filtersToAdd, replace: true }), source);
  };

  onClear = () => {
    this.setState({
      startDate: null,
      endDate: null,
    }, () => this.onApply);
  };

  onSelectDate = (date) => {
    const { startDate, endDate } = this.state;
    let newFrom = startDate;
    let newTo = endDate;
    if (startDate && endDate) {
      newFrom = date;
      newTo = null;
    } else if (!startDate || isBefore(date, startDate) || isBefore(date, endDate) || this.isPristine()) {
      newFrom = date;
      newTo = null;
    } else if (startDate && isAfter(date, startDate)) {
      newTo = date;
    }

    const dateFrom = newFrom ? formatDate(newFrom, 'yyyy-MM-dd') : '-';
    const dateTo = newTo ? formatDate(newTo, 'yyyy-MM-dd') : '-';
    analyticsTrack(
      { type: 'click', subject: `date selection`, value: { dateFrom, dateTo }, valueString: `${dateFrom} / ${dateTo}` },
      { category: 'Search page', action: 'Filter date', label: `${dateFrom} / ${dateTo}` }
    );

    this.setState({
      startDate: newFrom,
      endDate: newTo,
    });
  };

  onClickNow = () => {
    trackAvailabilityButton('Now');
    this.onAvailabilityChange(AVAILABILITY_FILTER_OPTIONS.NOW);
  };

  onClickToday = () => {
    trackAvailabilityButton('Today');
    this.onAvailabilityChange(AVAILABILITY_FILTER_OPTIONS.TODAY);
  };

  onClickTomorrow = () => {
    trackAvailabilityButton('Tomorrow');
    this.onAvailabilityChange(AVAILABILITY_FILTER_OPTIONS.TOMORROW);
  };

  onAvailabilityChange = (availability: AVAILABILITY_FILTER_OPTIONS) => {
    this.props.flux.actions.filters.setAvailabilityFilter({ availability });

    // set from date to relevant availability date
    const newDate = availability === 'tomorrow' ? addDays(new Date(), 1) : new Date();
    const fromDateFilter = getFilter('date.from');
    fromDateFilter.value = formatDate(newDate, 'yyyy-MM-dd');

    // unset to date
    const toDateFilter = getFilter('date.to');
    toDateFilter.value = null;

    // set availability filter
    const availabilityFilter = getFilter('other.availability.items[]');
    availabilityFilter.subOptions = availabilityFilter.subOptions.filter(subOption => subOption.value === availability);

    this.props.onClose(() => routeToSearchURL({ filtersToAdd: [availabilityFilter, toDateFilter, fromDateFilter], replace: true }), 'availability button');
  };

  render() {
    return (
      <AgendaPopover
        showAvailabilityOptions={this.props.showAvailabilityOptions}
        onClickNow={this.onClickNow}
        onClickToday={this.onClickToday}
        onClickTomorrow={this.onClickTomorrow}
        startDate={this.state.startDate}
        endDate={this.state.endDate}
        onApply={this.onApply}
        onClear={this.onClear}
        onSelectDate={this.onSelectDate}
      />
    );
  }
}

const mapStateToProps = (flux) => {
  const filters = flux.stores.FiltersStore.getFilters();
  const aggregations = flux.stores.SearchStore.getAggregations(filters);

  const availabilityFilter = filters.activeFilters.find(filter => filter.uid === 'other.availability.items[]');
  const nowFilterEnabled = !!availabilityFilter && availabilityFilter.subOptions.some(subOpt => subOpt.value === AVAILABILITY_FILTER_OPTIONS.NOW);

  return {
    filters,
    nowFilterEnabled,
    showAvailabilityOptions: showAvailabilityFilter(aggregations),
  };
};

const enhance = compose<AgendaPopoverInnerProps, AgendaPopoverOuterProps>(
  addFlux,
  withRouter,
  fluxxorConnect(['FiltersStore', 'SearchStore'], mapStateToProps)
);

export default enhance(AgendaPopoverContainer);
